import type { ReactNode } from "react";
import { OctagonX } from "lucide-react";

import { cn } from "@/lib/utils";
import { useHealth } from "../../hooks/useHealth";
import { useSystemState } from "../../hooks/useSystemState";
import { useWsConnection } from "../../hooks/useWsConnection";
import { TradingDataContext, type TradingData } from "@/context/TradingDataContext";
import AppHeader from "./AppHeader";
import LiveBanner from "../dashboard/LiveBanner";
import Footer from "../dashboard/Footer";

interface AppShellProps {
  /** Constrain the main content width (default true → centered max-width). */
  contained?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * The standard authenticated page frame — used by EVERY page so the chrome is
 * identical everywhere: the §19 LIVE banner, the emergency-stop strip, the unified
 * {@link AppHeader} (brand + live price + nav + system cluster), and the
 * version/commit Footer.
 *
 * <p>It owns the single session WebSocket ({@code /ws/trading}) + system-state +
 * health polls and publishes them through {@link TradingDataContext}, so the header
 * shows the live price on every page and the Dashboard body reads the SAME socket
 * (one subscription per shell, no duplicates).
 *
 * <p>§25 honesty: while /api/health is unknown the LIVE banner renders NOTHING (a
 * phantom banner is a false signal) and the footer says "?" instead of inventing a
 * version.
 */
export default function AppShell({ contained = true, className, children }: AppShellProps) {
  const ws = useWsConnection("/ws/trading");
  const systemState = useSystemState();
  const health = useHealth();

  const value: TradingData = {
    wsStatus: ws.status,
    wsEvents: ws.events,
    latestPrice: ws.latestPrice,
    marketStatus: ws.marketStatus,
    streamHealth: ws.streamHealth,
    systemState,
    health,
  };

  return (
    <TradingDataContext.Provider value={value}>
      <div className="text-foreground flex min-h-dvh flex-col">
        <LiveBanner mode={health?.mode} />
        {systemState.emergencyStop === true && (
          <div
            className="bg-live animate-glow-pulse relative z-30 flex items-center justify-center gap-2 px-4 py-1.5 text-center text-sm font-bold tracking-wide text-white"
            role="alert"
            aria-label="Arrêt d'urgence enclenché"
          >
            <OctagonX className="size-4" aria-hidden />
            ARRÊT D'URGENCE ENCLENCHÉ — le trading automatique est stoppé
          </div>
        )}
        <AppHeader />
        <main
          className={cn(
            "animate-fade-in-up w-full flex-1 px-4 py-5",
            contained && "mx-auto max-w-[1400px]",
            className,
          )}
        >
          {children}
        </main>
        <Footer health={health} />
      </div>
    </TradingDataContext.Provider>
  );
}
